<template>
  <v-row v-if="payerQuestions.length" class="mt-8 mb-8">
    <v-col cols="12">
      <h3 class="text-primary weight-600 mb-4">{{ props.title }}</h3>
      <div v-for="(group, gIndex) in groupedQuestions" :key="group.header || gIndex">
        <v-divider v-if="gIndex !== 0 && group.header && groupHasVisibleQuestions(group)" class="my-4"></v-divider>
        <h3 v-if="group.header && groupHasVisibleQuestions(group)" class="text-h6 mb-6">{{ group.header }}</h3>
        <div
          v-for="(currentRequirement, reqIndex) in group.questions"
          :key="currentRequirement.requirementRuleName"
          :class="{
            'd-none': !isRequirementApplicable(currentRequirement),
            'policy-flagged-question': currentRequirement.policyReview?.alignsWithPolicy === 'NO',
          }"
          class="mb-6">
          <p class="text-body-3-2 weight-600 text-primary mb-2">
            {{ currentRequirement.label }}
          </p>
          <div v-if="props.isEditable">
            <div v-if="currentRequirement.requirementOptions[0].nodeType === 'radio'">
              <FeedbackInputWrapper
                :field-id="currentRequirement.requirementRuleName"
                :order-key="props.orderKey"
                :model-value="
                  currentRequirement.selectedOption.id + ' ' + currentRequirement.selectedOption.label || ''
                "
                :label="currentRequirement.label">
                <template #default="{ onInput, onChange, onFocus, onBlur }">
                  <v-select
                    :id="currentRequirement.requirementRuleName"
                    v-model="currentRequirement.selectedOption"
                    :items="currentRequirement.requirementOptions"
                    item-title="label"
                    item-value="optionRuleName"
                    clearable
                    class="lh--force-white-background mb-2"
                    @input="onInput"
                    @update:model-value="
                      (e) => {
                        if (!e) {
                          // Already cleared — no-op on repeated X clicks
                          if (!currentRequirement.selectedOption?.id) {
                            currentRequirement.selectedOption = { id: null, label: null };
                            return;
                          }
                          const optionToUnset = {
                            id: currentRequirement.selectedOption.id,
                            label: currentRequirement.selectedOption.label,
                          };
                          currentRequirement.selectedOption = { id: null, label: null };
                          onChange(null);
                          unSetDependentSelection(currentRequirement, optionToUnset);
                          currentRequirement.originalSelectedOption = null;
                          return;
                        }
                        const selectedOption = currentRequirement.requirementOptions.find(
                          (option) => option.optionRuleName === e
                        );
                        currentRequirement.selectedOption = {
                          id: selectedOption.optionRuleName,
                          label: selectedOption.label,
                        };
                        onChange(currentRequirement.selectedOption);
                        setSessionRequirement(currentRequirement);
                      }
                    "
                    @focus="onFocus"
                    @blur="onBlur" />
                </template>
              </FeedbackInputWrapper>
            </div>
            <div v-else-if="currentRequirement.requirementOptions[0].nodeType === 'checkbox'">
              <FeedbackInputWrapper
                :field-id="currentRequirement.requirementRuleName"
                :order-key="props.orderKey"
                :model-value="getCheckboxModelValue(currentRequirement)"
                :label="currentRequirement.label">
                <template #default="{ onInput, onChange, onFocus, onBlur }">
                  <v-select
                    :id="currentRequirement.requirementRuleName"
                    v-model="currentRequirement.selectedOptions"
                    :items="currentRequirement.requirementOptions"
                    item-title="label"
                    item-value="optionRuleName"
                    multiple
                    chips
                    closable-chips
                    class="lh--force-white-background mb-2"
                    @input="onInput"
                    @update:model-value="
                      (selectedValues) => {
                        const selectedOptions = currentRequirement.requirementOptions.filter((option) =>
                          selectedValues.includes(option.optionRuleName)
                        );
                        currentRequirement.selectedOptions = selectedOptions;
                        onChange(selectedOptions);
                        setSessionRequirement(currentRequirement);
                      }
                    "
                    @focus="onFocus"
                    @blur="onBlur" />
                </template>
              </FeedbackInputWrapper>
            </div>
            <div v-else-if="currentRequirement.requirementOptions[0].nodeType === 'textarea'" class="mb-6">
              <FeedbackInputWrapper
                :field-id="`${currentRequirement.requirementOptions[0].optionRuleName}`"
                :order-key="props.orderKey"
                :model-value="currentRequirement.selectedOption.label"
                :label="currentRequirement.label">
                <template #default="{ onInput, onChange, onFocus, onBlur }">
                  <v-textarea
                    :id="`${currentRequirement.requirementOptions[0].optionRuleName}`"
                    v-model="currentRequirement.selectedOption.label"
                    :placeholder="currentRequirement.requirementOptions[0].label"
                    class="mb-4 lh--force-white-background"
                    @input="onInput"
                    @update:model-value="
                      (e) => {
                        onChange(e);
                        setSessionRequirement(currentRequirement);
                      }
                    "
                    @focus="onFocus"
                    @blur="onBlur" />
                </template>
              </FeedbackInputWrapper>
            </div>
            <div v-else class="mt-4">
              <FeedbackInputWrapper
                :field-id="`${currentRequirement.requirementOptions[0].optionRuleName}`"
                :order-key="props.orderKey"
                :model-value="currentRequirement.selectedOption.label"
                :label="currentRequirement.label">
                <template #default="{ onInput, onChange, onFocus, onBlur }">
                  <v-text-field
                    :id="`${currentRequirement.requirementOptions[0].optionRuleName}`"
                    v-model="currentRequirement.selectedOption.label"
                    :placeholder="currentRequirement.requirementOptions[0].label"
                    class="mb-2 lh--force-white-background"
                    @input="onInput"
                    @change="
                      (e) => {
                        onChange(e);
                        setSessionRequirement(currentRequirement);
                      }
                    "
                    @focus="onFocus"
                    @blur="onBlur" />
                </template>
              </FeedbackInputWrapper>
            </div>
            <v-alert
              v-if="
                isRequirementApplicable(currentRequirement) &&
                props.formButtonClicked &&
                !isAnswerValid(currentRequirement)
              "
              type="error"
              variant="tonal"
              density="compact"
              class="mb-4 form-validation-alert">
              Please answer this question to the best of your ability. Failing to do so may result in a delay in
              processing your request.
            </v-alert>
          </div>
          <div v-else>
            <v-textarea
              v-if="currentRequirement.requirementOptions[0].nodeType === 'textarea'"
              :id="`${currentRequirement.requirementOptions[0].optionRuleName}`"
              :model-value="currentRequirement.selectedOption?.label"
              readonly
              disabled
              class="lh--force-white-background mb-2" />
            <v-text-field
              v-else
              :id="`${currentRequirement.requirementOptions[0].optionRuleName}`"
              :model-value="currentRequirement.selectedOption?.label"
              readonly
              disabled
              class="lh--force-white-background mb-2" />
          </div>
          <div v-if="currentRequirement.llmPrediction" class="mb-6">
            <p class="text-body-3-1">
              <v-chip
                v-if="currentRequirement.llmPrediction.isHumanReviewRequested"
                color="error"
                size="small"
                class="mr-2">
                Review Needed
              </v-chip>
              <span
                v-if="currentRequirement.llmPrediction.context"
                class="text-body-3-1 text-color-blue-gray weight-400">
                Context: {{ currentRequirement.llmPrediction.context }}
              </span>
            </p>
          </div>
          <div v-if="currentRequirement.policyReview?.alignsWithPolicy === 'NO'" class="policy-warning-inline mb-4">
            <v-icon color="error" size="18" class="mr-1">mdi-alert-circle</v-icon>
            <span class="policy-warning-text">The current answer is unlikely to be approved.</span>
          </div>
          <v-divider v-if="reqIndex !== group.questions.length - 1"></v-divider>
        </div>
      </div>
    </v-col>
  </v-row>
</template>

<script setup>
import { ref, onMounted, computed, watch, nextTick } from "vue";
import FeedbackInputWrapper from "@/components/FeedbackInputWrapper.vue";

const payerQuestions = ref([]);
const sessionRequirements = ref([]);
const sessionRequirementOptions = ref([]);
const portalPayerQuestionsJson = ref([]);

const emit = defineEmits(["updateChecklist"]);

const props = defineProps({
  orderKey: {
    type: String,
    default() {
      return "";
    },
  },
  payerQuestions: {
    type: Array,
    default() {
      return [];
    },
  },
  portalPayerQuestionsJson: {
    type: Array,
    default() {
      return [];
    },
  },
  checklist: {
    type: Array,
    default() {
      return [];
    },
  },
  llmPrediction: {
    type: Array,
    default() {
      return [];
    },
  },
  formButtonClicked: {
    type: Boolean,
    default() {
      return false;
    },
  },
  title: {
    type: String,
    default: "",
  },
  submissionMethod: {
    type: String,
    default: "",
  },
  isEditable: {
    type: Boolean,
    default: true,
  },
  policyReviews: {
    type: Array,
    default() {
      return [];
    },
  },
});

onMounted(async () => {
  // Initialize portalPayerQuestionsJson from props
  if (props.portalPayerQuestionsJson && props.portalPayerQuestionsJson.length > 0) {
    portalPayerQuestionsJson.value = props.portalPayerQuestionsJson;
  }

  let payerQuestionsTemp = [];
  if (props.payerQuestions && props.payerQuestions.length > 0) {
    for (const requirement of props.payerQuestions) {
      let payerQuestion = requirement;
      const checklistSelection = props.checklist.find(
        (selection) =>
          selection.id === requirement.requirementRuleName &&
          selection.options &&
          selection.options.length > 0 &&
          selection.options.some((option) => option.label)
      );
      if (checklistSelection) {
        if (requirement.requirementOptions[0].nodeType === "checkbox") {
          // Map checklist options to match the structure expected by v-select
          payerQuestion.selectedOptions = checklistSelection.options.map((checklistOption) => {
            const matchingOption = requirement.requirementOptions.find(
              (reqOption) => reqOption.optionRuleName === checklistOption.id
            );
            return matchingOption || checklistOption;
          });
          payerQuestion.originalSelectedOptions = payerQuestion.selectedOptions;
          sessionRequirements.value.push(requirement.requirementRuleName);
          sessionRequirementOptions.value.push(...checklistSelection.options.map((option) => option.id));
        } else {
          payerQuestion.selectedOption =
            checklistSelection.options.length > 1 ? checklistSelection.options : checklistSelection.options[0];
          payerQuestion.originalSelectedOption =
            checklistSelection.options.length > 1 ? checklistSelection.options : checklistSelection.options[0];
          sessionRequirements.value.push(requirement.requirementRuleName);
          sessionRequirementOptions.value.push(requirement.selectedOption.id);
        }
        if (props.llmPrediction && props.llmPrediction.length > 0) {
          payerQuestion.llmPrediction = props.llmPrediction.find(
            (prediction) => prediction.answerId === requirement.requirementRuleName
          );
        }
        // Match policy review (policy alignment check)
        if (props.policyReviews && props.policyReviews.length > 0) {
          payerQuestion.policyReview = props.policyReviews.find(
            (review) => review.questionId === requirement.requirementRuleName
          );
        }
      } else {
        // Check if requirement already has existing selections that should be preserved
        const hasExistingSelections =
          (payerQuestion.requirementOptions[0].nodeType === "radio" &&
            payerQuestion.selectedOption &&
            payerQuestion.selectedOption.id) ||
          (payerQuestion.requirementOptions[0].nodeType === "checkbox" &&
            payerQuestion.selectedOptions &&
            payerQuestion.selectedOptions.length > 0);

        if (hasExistingSelections) {
          // Preserve existing selections and add to session
          if (payerQuestion.requirementOptions[0].nodeType === "radio") {
            sessionRequirements.value.push(requirement.requirementRuleName);
            sessionRequirementOptions.value.push(payerQuestion.selectedOption.id);
          } else if (payerQuestion.requirementOptions[0].nodeType === "checkbox") {
            sessionRequirements.value.push(requirement.requirementRuleName);
            sessionRequirementOptions.value.push(
              ...payerQuestion.selectedOptions.map((option) => option.optionRuleName)
            );
          }
        } else {
          // Only initialize empty selections if there are no existing selections
          if (payerQuestion.requirementOptions[0].nodeType === "radio") {
            payerQuestion.selectedOption = { id: null, label: null };
          } else if (payerQuestion.requirementOptions[0].nodeType === "checkbox") {
            payerQuestion.selectedOptions = [];
          }
        }
        payerQuestion.llmPrediction = null;
        if (
          payerQuestion.requirementOptions[0].nodeType === "text" ||
          payerQuestion.requirementOptions[0].nodeType === "textarea" ||
          payerQuestion.requirementOptions[0].nodeType === "number" ||
          payerQuestion.requirementOptions[0].nodeType === "date"
        ) {
          payerQuestion.selectedOption = {
            id: payerQuestion.requirementOptions[0].optionRuleName,
            label: null,
          };
          payerQuestion.llmPrediction = null;
        } else {
          payerQuestion.selectedOption = { id: null, label: null };
          payerQuestion.llmPrediction = null;
        }
      }
      payerQuestionsTemp.push(requirement);
    }
    payerQuestions.value.push(...sortRequirements(payerQuestionsTemp));
  }

  // Emit all currently visible/answered questions to filter checklist
  // This ensures only visible/answered questions are saved, even if user doesn't make edits
  nextTick(() => {
    emitVisibleAnsweredQuestions();
  });
});

// Function to emit all currently visible/answered questions
function emitVisibleAnsweredQuestions() {
  // Determine which questions are visible
  let questionIds;
  if (visibleQuestionChain.value && visibleQuestionChain.value.length > 0) {
    // EPA: use chain-based visibility
    questionIds = visibleQuestionChain.value;
  } else {
    // No next_question logic (e.g., CMM fax forms): all answered questions are visible
    questionIds = sessionRequirements.value;
    if (!questionIds || questionIds.length === 0) return;
  }

  const visibleAnsweredQuestions = [];
  for (const questionId of questionIds) {
    const requirement = payerQuestions.value.find((req) => req.requirementRuleName === questionId);
    if (!requirement) continue;

    // Only include questions that have answers
    if (!sessionRequirements.value.includes(questionId)) continue;

    // Build checklist item based on question type
    if (requirement.requirementOptions[0].nodeType === "checkbox") {
      if (requirement.selectedOptions && requirement.selectedOptions.length > 0) {
        visibleAnsweredQuestions.push({
          id: requirement.requirementRuleName,
          label: requirement.label,
          options: requirement.selectedOptions.map((option) => ({
            id: option.optionRuleName,
            label: option.label,
          })),
        });
      }
    } else {
      if (requirement.selectedOption && requirement.selectedOption.id) {
        visibleAnsweredQuestions.push({
          id: requirement.requirementRuleName,
          label: requirement.label,
          options: [
            {
              id: requirement.selectedOption.id,
              label: requirement.selectedOption.label,
            },
          ],
        });
      }
    }
  }

  if (visibleAnsweredQuestions.length > 0) {
    emit("updateChecklist", visibleAnsweredQuestions);
  }
}

function sortRequirements(requirements) {
  const sortedRequirements = [];
  if (
    requirements &&
    requirements.length > 0 &&
    requirements[0].displayOrder !== undefined &&
    requirements[0].displayOrder !== null
  ) {
    requirements.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    sortedRequirements.push(...requirements);
    return sortedRequirements;
  }
  const requirementsWithRules = [];
  const requirementsWithNoRules = [];
  for (const requirement of requirements) {
    if (requirement.requirementRuleSet && requirement.requirementRuleSet.length > 0) {
      requirementsWithRules.push(requirement);
    } else {
      requirementsWithNoRules.push(requirement);
    }
  }
  requirementsWithRules.sort((a, b) => {
    if (a.requirementRuleSet.includes(b.requirementRuleName)) {
      return 1;
    } else if (b.requirementRuleSet.includes(a.requirementRuleName)) {
      return -1;
    } else {
      return 0;
    }
  });
  sortedRequirements.push(...requirementsWithNoRules);
  sortedRequirements.push(...requirementsWithRules);
  return sortedRequirements;
}

function unSetDependentSelection(unselectedRequirement, unselectedOption) {
  const unSetRequirements = [];
  sessionRequirementOptions.value = sessionRequirementOptions.value.filter(
    (sessionOption) => sessionOption !== unselectedOption.id
  );
  sessionRequirements.value = sessionRequirements.value.filter(
    (sessionRequirement) => sessionRequirement !== unselectedRequirement.requirementRuleName
  );
  unSetRequirements.push({
    id: unselectedRequirement.requirementRuleName,
  });
  for (const requirement of payerQuestions.value) {
    if (
      requirement.requirementRuleSet &&
      requirement.requirementRuleSet.includes(unselectedRequirement.requirementRuleName)
    ) {
      for (const option of requirement.requirementOptions) {
        if (option.optionRuleSet && option.optionRuleSet.includes(unselectedOption.id)) {
          sessionRequirementOptions.value = sessionRequirementOptions.value.filter(
            (sessionOption) => sessionOption !== option.optionRuleName
          );
          sessionRequirements.value = sessionRequirements.value.filter(
            (sessionRequirement) => sessionRequirement !== requirement.requirementRuleName
          );
          if (requirement.requirementOptions[0].nodeType === "radio") {
            requirement.selectedOption = { id: null, label: null };
          } else if (requirement.requirementOptions[0].nodeType === "checkbox") {
            requirement.selectedOptions = [];
          }
          if (
            requirement.requirementOptions[0].nodeType === "text" ||
            requirement.requirementOptions[0].nodeType === "textarea" ||
            requirement.requirementOptions[0].nodeType === "number" ||
            requirement.requirementOptions[0].nodeType === "date"
          ) {
            requirement.selectedOption = {
              id: requirement.requirementOptions[0].optionRuleName,
              label: null,
            };
          } else {
            requirement.selectedOption = { id: null, label: null };
          }
          unSetRequirements.push({
            id: requirement.requirementRuleName,
          });
        }
      }
    }
  }
  emit("updateChecklist", unSetRequirements, true);
}

function setSessionRequirement(requirement) {
  if (sessionRequirements.value.includes(requirement.requirementRuleName)) {
    if (requirement.requirementOptions[0].nodeType === "checkbox" && requirement.originalSelectedOptions) {
      // For checkbox, we need to unset each original option
      requirement.originalSelectedOptions.forEach((originalOption) => {
        unSetDependentSelection(requirement, originalOption);
      });
    } else if (requirement.originalSelectedOption) {
      unSetDependentSelection(requirement, requirement.originalSelectedOption);
    }
  }
  if (!sessionRequirements.value.includes(requirement.requirementRuleName)) {
    sessionRequirements.value.push(requirement.requirementRuleName);
  }
  if (requirement.requirementOptions[0].nodeType === "checkbox") {
    // Handle checkbox multi-select
    const selectedOptionIds = requirement.selectedOptions.map((option) => option.optionRuleName);
    sessionRequirementOptions.value = sessionRequirementOptions.value.filter(
      (option) => !requirement.requirementOptions.map((option) => option.optionRuleName).includes(option)
    );
    sessionRequirementOptions.value.push(...selectedOptionIds);
  } else if (!sessionRequirementOptions.value.includes(requirement.selectedOption.id)) {
    if (requirement.requirementOptions[0].nodeType === "radio") {
      sessionRequirementOptions.value = sessionRequirementOptions.value.filter(
        (option) => !requirement.requirementOptions.map((option) => option.optionRuleName).includes(option)
      );
    }
    sessionRequirementOptions.value.push(requirement.selectedOption.id);
  }
  if (requirement.requirementOptions[0].nodeType === "checkbox") {
    requirement.originalSelectedOptions = requirement.selectedOptions;
  } else {
    requirement.originalSelectedOption = requirement.selectedOption;
  }
  emitVisibleAnsweredQuestions();
}

function isAnswerValid(requirement) {
  if (requirement.requirementOptions[0].nodeType === "checkbox") {
    // For checkbox, check if at least one option is selected
    if (props.submissionMethod === "eFax") {
      if (requirement.isRequired && (!requirement.selectedOptions || requirement.selectedOptions.length === 0)) {
        return false;
      } else {
        return true;
      }
    } else {
      return requirement.selectedOptions && requirement.selectedOptions.length > 0;
    }
  } else {
    // Handle radio and other input types
    if (props.submissionMethod === "eFax") {
      if (requirement.isRequired && !requirement.selectedOption.id) {
        return false;
      } else {
        return true;
      }
    } else {
      if (!requirement.selectedOption.id) {
        return false;
      } else if (
        requirement.selectedOption.id &&
        (requirement.requirementOptions[0].nodeType === "text" ||
          requirement.requirementOptions[0].nodeType === "textarea" ||
          requirement.requirementOptions[0].nodeType === "number" ||
          requirement.requirementOptions[0].nodeType === "date") &&
        !requirement.selectedOption.label
      ) {
        return false;
      } else if (requirement.selectedOption.label && requirement.validationRule) {
        const regex = new RegExp(requirement.validationRule);
        const selectedOption = requirement.selectedOption.label.trim();
        return regex.test(selectedOption);
      } else {
        return true;
      }
    }
  }
}

// Build sequential question chain based on answers
// Returns array of requirement IDs that should be visible
const visibleQuestionChain = computed(() => {
  if (!portalPayerQuestionsJson.value || portalPayerQuestionsJson.value.length === 0) {
    // No portalPayerQuestionsJson - return null to use old logic
    return null;
  }

  const visibleIds = new Set();
  const visited = new Set();

  if (payerQuestions.value.length === 0) {
    return [];
  }

  const normalizedRequirementIds = new Set(
    payerQuestions.value.map((req) => normalizeQuestionId(req.requirementRuleName))
  );

  // Find the minimum order value in portalPayerQuestionsJson (coerce to number in case API returns string)
  const toOrderNum = (v) => {
    if (v == null || v === "") return Infinity;
    const n = Number(v);
    return n === n ? n : Infinity;
  };
  const orders = portalPayerQuestionsJson.value.map((q) => toOrderNum(q.order));
  const minOrder = Math.min(...orders);

  // Root questions: lowest section order and portal name matches any payer requirement (parallel CMM sections).
  const rootQuestions = portalPayerQuestionsJson.value.filter((q) => {
    const qOrder = toOrderNum(q.order);
    return qOrder === minOrder && normalizedRequirementIds.has(q.name);
  });

  // Build chain starting from each root question
  for (const rootQ of rootQuestions) {
    buildQuestionChain(rootQ, visibleIds, visited);
  }

  const result = Array.from(visibleIds);
  return result;
});

// Watch visible question chain and emit visible/answered questions when it changes
watch(
  visibleQuestionChain,
  () => {
    // Emit visible/answered questions when chain changes (e.g., after answering a question)
    nextTick(() => {
      emitVisibleAnsweredQuestions();
    });
  },
  { deep: true }
);

// Recursively build question chain based on answers
function buildQuestionChain(portalQuestion, visibleIds, visited) {
  const questionId = portalQuestion.name;

  // Prevent infinite loops
  if (visited.has(questionId)) {
    return;
  }
  visited.add(questionId);

  // Find corresponding requirement in payerQuestions
  const requirement = payerQuestions.value.find((req) => {
    const normalizedId = normalizeQuestionId(req.requirementRuleName);
    return normalizedId === questionId;
  });

  if (!requirement) {
    return; // Question not found in payerQuestions
  }

  // Check if question is invisible
  if (requirement.invisibleInPortal) {
    return;
  }

  // Check if condition_groups are satisfied
  const conditionGroupsSatisfied = areConditionGroupsSatisfied(portalQuestion);
  if (!conditionGroupsSatisfied) {
    return; // Condition groups not met
  }

  // Add question to visible chain
  visibleIds.add(requirement.requirementRuleName);

  // Check if question has an answer
  // A question has an answer if:
  // 1. It's in sessionRequirements (question was answered)
  // 2. AND it has at least one option in sessionRequirementOptions that belongs to it
  const hasAnswer = sessionRequirements.value.includes(requirement.requirementRuleName);

  if (!hasAnswer) {
    // Stop here - wait for answer before continuing
    return;
  }

  // Find the selected answer(s) for this question
  const selectedOptions = sessionRequirementOptions.value.filter((optId) => {
    // Check if this option belongs to this question
    // Option format: "questionId==optionValue" or "questionId_text_input" or just "optionValue"
    const optionPart = optId.split("==")[0];
    // Remove _text_input suffix if present for comparison
    const optionWithoutSuffix = optionPart.replace(/_text_input$/, "");
    const normalizedQuestionId = normalizeQuestionId(optionWithoutSuffix);
    return normalizedQuestionId === questionId;
  });

  // Check question type to determine how to get next_question
  let questionItem = null;
  for (const item of portalQuestion.questions || []) {
    if (item.name === questionId || item.type !== "statement") {
      questionItem = item;
      break;
    }
  }

  // Handle text/textarea/number/date inputs - they have next_question directly on the question item
  if (
    questionItem &&
    (questionItem.type === "text" ||
      questionItem.type === "textarea" ||
      questionItem.type === "number" ||
      questionItem.type === "date")
  ) {
    // For text inputs, check if question has an answer (it's in sessionRequirements)
    if (selectedOptions.length > 0 || sessionRequirements.value.includes(requirement.requirementRuleName)) {
      // Get next_question from the question item itself, or from the question object
      const nextQuestionName =
        questionItem.next_question ||
        questionItem.nextQuestion ||
        portalQuestion.next_question ||
        portalQuestion.nextQuestion;
      if (!nextQuestionName || nextQuestionName === "END") {
        // Still check condition_groups for dependent questions
        checkQuestionsWithConditionGroups(questionId, visibleIds, visited);
        return;
      }

      // Find next question in portalPayerQuestionsJson
      const nextQuestion = findQuestionInPortalJson(nextQuestionName, true);
      if (nextQuestion) {
        // Recursively build chain from next question
        buildQuestionChain(nextQuestion, visibleIds, visited);
      } else {
        // Check condition_groups for dependent questions
        checkQuestionsWithConditionGroups(questionId, visibleIds, visited);
      }
    }
    return;
  }

  // Handle radio/checkbox - they have next_question on choices
  if (selectedOptions.length === 0) {
    // Question is marked as answered but no options found - stop here
    return;
  }

  // Get all available choice names from portalPayerQuestionsJson for validation
  const availableChoiceNames = new Set();
  for (const questionItem of portalQuestion.questions || []) {
    if (questionItem.type === "radio" || questionItem.type === "checkbox") {
      for (const choice of questionItem.choices || []) {
        availableChoiceNames.add(choice.name);
      }
    }
  }

  // For checkbox questions, the question item itself may have next_question
  // (e.g., "43119" -> "43119-Numeric" regardless of which choices are selected)
  if (questionItem && questionItem.type === "checkbox") {
    const questionNextQuestion = questionItem.next_question || questionItem.nextQuestion;
    if (questionNextQuestion && questionNextQuestion !== "END") {
      const nextQuestion = findQuestionInPortalJson(questionNextQuestion, true);
      if (nextQuestion) {
        // Recursively build chain from next question
        buildQuestionChain(nextQuestion, visibleIds, visited);
      }
    }
  }

  // Find next question based on selected answer
  // For radio, use the single selected option's next_question
  // For checkbox, check each selected choice's next_question (may have multiple paths)
  for (const selectedOptId of selectedOptions) {
    const normalizedOptId = normalizeOptionId(selectedOptId);

    // Validate that the selected option matches an available choice
    if (!availableChoiceNames.has(normalizedOptId)) {
      // Clear the invalid answer from session to prevent issues
      sessionRequirements.value = sessionRequirements.value.filter(
        (reqId) => reqId !== requirement.requirementRuleName
      );
      sessionRequirementOptions.value = sessionRequirementOptions.value.filter((optId) => optId !== selectedOptId);
      // Clear the selected option from the requirement
      if (requirement.requirementOptions[0].nodeType === "radio") {
        requirement.selectedOption = { id: null, label: null };
      } else if (requirement.requirementOptions[0].nodeType === "checkbox") {
        requirement.selectedOptions = requirement.selectedOptions.filter(
          (opt) => normalizeOptionId(opt.optionRuleName) !== normalizedOptId
        );
      }
      // Stop here - invalid answer, wait for user to select a valid option
      return;
    }

    let foundChoice = false;

    // Find the choice in portalPayerQuestionsJson
    for (const questionItem of portalQuestion.questions || []) {
      if (questionItem.type === "radio" || questionItem.type === "checkbox") {
        for (const choice of questionItem.choices || []) {
          if (choice.name === normalizedOptId) {
            foundChoice = true;
            // Handle both snake_case and camelCase property names
            const nextQuestionName = choice.next_question || choice.nextQuestion;
            if (!nextQuestionName || nextQuestionName === "END") {
              // This choice doesn't lead to a next question, continue to next selected option
              break;
            }

            // Check if nextQuestionName is a nested question item within the current question
            // (e.g., "1067028_978299" is a question item within question "1067028")
            let nestedQuestionItem = null;
            for (const item of portalQuestion.questions || []) {
              if (item.name === nextQuestionName) {
                nestedQuestionItem = item;
                break;
              }
            }

            // First, check if nextQuestionName is a separate top-level question
            let nextQuestion = findQuestionInPortalJson(nextQuestionName, true);

            if (nestedQuestionItem && !nextQuestion) {
              // It's a nested question item but not a separate top-level question
              // Check if there's a requirement for this nested question item
              const nestedRequirement = payerQuestions.value.find((req) => {
                const normalizedId = normalizeQuestionId(req.requirementRuleName);
                return normalizedId === nextQuestionName;
              });

              if (nestedRequirement) {
                // There's a requirement for this nested question item - treat it as a separate question
                // Create a virtual question object from the nested item
                const virtualQuestion = {
                  name: nextQuestionName,
                  order: portalQuestion.order,
                  questions: [nestedQuestionItem],
                  condition_groups: nestedQuestionItem.condition_groups || [],
                  invisible_to_user: false,
                };
                // Process the nested question item as a separate question
                buildQuestionChain(virtualQuestion, visibleIds, visited);
              } else {
                // No requirement for nested item - just follow its next_question
                const nestedNextQuestionName = nestedQuestionItem.next_question || nestedQuestionItem.nextQuestion;
                if (!nestedNextQuestionName || nestedNextQuestionName === "END") {
                  break;
                }
                // Find the actual next question
                nextQuestion = findQuestionInPortalJson(nestedNextQuestionName, true);
                if (nextQuestion) {
                  // Recursively build chain from next question
                  buildQuestionChain(nextQuestion, visibleIds, visited);
                }
              }
            } else if (nextQuestion) {
              // nextQuestionName is a separate top-level question - process it
              // Recursively build chain from next question
              buildQuestionChain(nextQuestion, visibleIds, visited);
            }
            // For checkbox, continue checking other selected options (multiple paths possible)
            // For radio, only one choice can be selected, so break
            if (questionItem.type === "radio") {
              break;
            }
          }
        }
        if (foundChoice && questionItem.type === "radio") {
          break; // Found the choice for radio, no need to check other question items
        }
      }
    }
  }

  // After processing all selected options, check if any other questions have condition_groups
  // that depend on this question and if those conditions are satisfied
  checkQuestionsWithConditionGroups(questionId, visibleIds, visited);
}

// Helper: Check if any questions have condition_groups that depend on the current question
function checkQuestionsWithConditionGroups(currentQuestionId, visibleIds, visited) {
  if (!portalPayerQuestionsJson.value || portalPayerQuestionsJson.value.length === 0) {
    return;
  }

  // Helper: Recursively check if condition_groups reference the current question
  function hasConditionForQuestion(conditionGroups, questionId) {
    if (!conditionGroups || conditionGroups.length === 0) {
      return false;
    }

    for (const group of conditionGroups) {
      // Check direct conditions
      const conditions = group.conditions || [];
      for (const condition of conditions) {
        if (condition.field === questionId) {
          return true;
        }
      }

      // Check nested condition_groups recursively
      // Handle both snake_case (condition_groups) and camelCase (conditionGroups)
      const nestedGroups = group.condition_groups || group.conditionGroups;
      if (nestedGroups && nestedGroups.length > 0) {
        if (hasConditionForQuestion(nestedGroups, questionId)) {
          return true;
        }
      }
    }

    return false;
  }

  // Check all questions in portalPayerQuestionsJson
  for (const question of portalPayerQuestionsJson.value) {
    // Skip if already visited
    if (visited.has(question.name)) {
      continue;
    }

    // Check if this question has condition_groups that reference the current question
    // Handle both snake_case (condition_groups) and camelCase (conditionGroups)
    const conditionGroups = question.condition_groups || question.conditionGroups;
    if (!conditionGroups || conditionGroups.length === 0) {
      continue;
    }

    const hasCondition = hasConditionForQuestion(conditionGroups, currentQuestionId);
    if (hasCondition) {
      // Check if condition_groups are satisfied
      const conditionGroupsSatisfied = areConditionGroupsSatisfied(question);
      if (conditionGroupsSatisfied) {
        // Process this question
        buildQuestionChain(question, visibleIds, visited);
      }
    }
  }
}

function isRequirementApplicable(requirement) {
  // Questions is not applicable if it is invisible in portal
  if (requirement.invisibleInPortal) {
    return false;
  }

  // If we have portalPayerQuestionsJson, use sequential chain logic
  if (portalPayerQuestionsJson.value && portalPayerQuestionsJson.value.length > 0) {
    return visibleQuestionChain.value.includes(requirement.requirementRuleName);
  }

  // Fallback to old logic if no portalPayerQuestionsJson
  // If question has already been answered, it needs to be visible
  if (sessionRequirements.value.includes(requirement.requirementRuleName)) {
    return true;
  }
  // if question has no rules attached, it needs to be visible
  if (!requirement.requirementRuleSet || requirement.requirementRuleSet.length === 0) {
    return true;
  }
  // if question rules are all met, check options rules for displaying options before deciding question visibility
  const allQuestionRulesMet = requirement.requirementRuleSet.every((rule) => sessionRequirements.value.includes(rule));
  if (!allQuestionRulesMet) {
    return false;
  }
  // Check if there are any answers that fail the rule check (existing answers do not include the answer that is required in the rules)
  let optionsFailedRuleCheck = [];
  for (const option of requirement.requirementOptions) {
    // If option has no optionRuleSet, it is always applicable (no filtering needed)
    if (!option.optionRuleSet || option.optionRuleSet.length === 0) {
      continue;
    }
    // If option has optionRuleSet, check if ALL rules are satisfied
    for (const optionRule of option.optionRuleSet) {
      if (!sessionRequirementOptions.value.includes(optionRule)) {
        optionsFailedRuleCheck.push(option);
        break; // No need to check remaining rules for this option
      }
    }
  }
  // Copy to JSON to avoid modifying the original object saved on the record
  let tempRequirement = JSON.parse(JSON.stringify(requirement));
  for (const option of optionsFailedRuleCheck) {
    tempRequirement.requirementOptions.splice(
      tempRequirement.requirementOptions.findIndex((o) => o.optionRuleName === option.optionRuleName),
      1
    );
  }
  // if there are no options left, the question is not applicable
  if (tempRequirement.requirementOptions.length === 0) {
    return false;
  } else {
    // Check elective rules for the options
    // A question is applicable if at least one option is visible
    // An option is visible if:
    // 1. It has no elective rules, OR
    // 2. It has elective rules AND each rule set has at least one rule satisfied
    let hasVisibleOption = false;
    for (const option of tempRequirement.requirementOptions) {
      const hasElectiveRules = option.electiveOptionRuleSet && option.electiveOptionRuleSet.length > 0;
      if (!hasElectiveRules) {
        // Option has no elective rules, so it's always visible
        return true;
      }
      // Filter out empty inner arrays from electiveOptionRuleSet
      const validRuleSets = option.electiveOptionRuleSet.filter((ruleSet) => ruleSet && ruleSet.length > 0);
      if (validRuleSets.length === 0) {
        // All rule sets are empty, treat as no elective rules
        return true;
      }
      // Check if at least one rule from EACH array is satisfied
      const allRuleSetsSatisfied = validRuleSets.every((ruleSet) =>
        ruleSet.some((rule) => sessionRequirementOptions.value.includes(rule))
      );
      if (allRuleSetsSatisfied) {
        // At least one rule from each array is satisfied, option is visible
        hasVisibleOption = true;
      }
    }
    // If we found at least one visible option, question is applicable
    if (hasVisibleOption) {
      return true;
    }
    // No options are visible after elective rule filtering
    return false;
  }
}

// Helper function to get model value for checkbox multi-select
const getCheckboxModelValue = (requirement) => {
  if (requirement.selectedOptions && requirement.selectedOptions.length > 0) {
    return requirement.selectedOptions.map((option) => option.optionRuleName);
  }
  return [];
};

const groupedQuestions = computed(() => {
  const map = new Map();

  payerQuestions.value.forEach((requirement) => {
    const header = requirement.sectionHeader || "no-header";
    if (!map.has(header)) {
      map.set(header, []);
    }
    map.get(header).push(requirement);
  });

  return Array.from(map, ([header, questions]) => ({
    header: header === "no-header" ? null : header,
    questions,
  }));
});

function groupHasVisibleQuestions(group) {
  return group.questions.some((req) => isRequirementApplicable(req));
}

// Part 1: Normalization functions for matching with portalPayerQuestionsJson
// Note: portalPayerQuestionsJson already has normalized names, so we only normalize payerQuestions/checklist IDs
function normalizeQuestionId(questionId) {
  // Remove prefix (characters before first _)
  if (!questionId) return "";
  const underscoreIndex = questionId.indexOf("_");
  if (underscoreIndex === -1) return questionId;
  return questionId.substring(underscoreIndex + 1);
}

function normalizeOptionId(optionId) {
  // Remove question id before ==, remove _text_input suffix
  // Matches backend: option_id.replace(prefix, "").split("==")[-1].replace("_text_input", "")
  if (!optionId) return "";

  // First remove prefix (characters before first _)
  let normalized = normalizeQuestionId(optionId);

  // Split by == and take the last part (the actual option value)
  const parts = normalized.split("==");
  normalized = parts.length > 1 ? parts[parts.length - 1] : normalized;

  // Remove _text_input suffix
  normalized = normalized.replace("_text_input", "");
  return normalized;
}

// Part 2: Find question in portalPayerQuestionsJson by normalized ID
// portalPayerQuestionsJson.name is already normalized
// If questionIdFromPayerQuestions comes from portalPayerQuestionsJson (like nextQuestion), it's already normalized
// If it comes from payerQuestions, it needs normalization
function findQuestionInPortalJson(questionIdFromPayerQuestions, isAlreadyNormalized = false) {
  if (!portalPayerQuestionsJson.value || portalPayerQuestionsJson.value.length === 0) {
    return null;
  }
  // If the ID is already normalized (e.g., from nextQuestion in portalPayerQuestionsJson), use it as-is
  // Otherwise, normalize it (e.g., from payerQuestions.requirementRuleName)
  const normalizedId = isAlreadyNormalized
    ? questionIdFromPayerQuestions
    : normalizeQuestionId(questionIdFromPayerQuestions);
  const found = portalPayerQuestionsJson.value.find((q) => q.name === normalizedId);
  return found;
}

// Part 3: Get next question based on current answer (for future use)
function _getNextQuestionFromPortalJson(currentQuestionId, selectedOptionId) {
  // currentQuestionId comes from payerQuestions.requirementRuleName (needs normalization)
  // selectedOptionId comes from payerQuestions.optionRuleName (needs normalization)
  const normalizedOptionId = normalizeOptionId(selectedOptionId);

  const portalQuestion = findQuestionInPortalJson(currentQuestionId);
  if (!portalQuestion) return null;

  // Find the choice that matches the selected option
  // portalPayerQuestionsJson.choice.name is already normalized
  for (const questionItem of portalQuestion.questions || []) {
    if (questionItem.type === "radio" || questionItem.type === "checkbox") {
      for (const choice of questionItem.choices || []) {
        // Compare normalized option ID from payerQuestions with normalized choice name from portal
        if (choice.name === normalizedOptionId) {
          return choice.next_question;
        }
      }
    }
  }
  return null;
}

// Helper: Check if a single condition is satisfied
function isConditionSatisfied(condition) {
  const field = condition.field; // Already normalized question ID
  const value = condition.value; // Already normalized option value
  const operator = condition.operator; // "eq", "ne", "lte", "gte", etc.

  // Find the requirement for the field in the condition
  const requirement = payerQuestions.value.find((req) => {
    const normalizedId = normalizeQuestionId(req.requirementRuleName);
    return normalizedId === field;
  });

  if (!requirement) {
    // Requirement not found, condition not satisfied
    return false;
  }

  // Handle numeric comparison operators (lte, gte, lt, gt)
  if (["lte", "gte", "lt", "gt"].includes(operator)) {
    // Get the actual answer value from the requirement
    // For text inputs, the value is stored in selectedOption.label
    let answerValue = null;
    if (requirement.selectedOption && requirement.selectedOption.label) {
      answerValue = parseFloat(requirement.selectedOption.label);
    }

    if (answerValue !== null && !isNaN(answerValue)) {
      const conditionValue = parseFloat(value);
      if (!isNaN(conditionValue)) {
        if (operator === "lte") {
          return answerValue <= conditionValue;
        } else if (operator === "gte") {
          return answerValue >= conditionValue;
        } else if (operator === "lt") {
          return answerValue < conditionValue;
        } else if (operator === "gt") {
          return answerValue > conditionValue;
        }
      }
    }
    return false;
  } else {
    // Handle equality/inequality operators (eq, ne)
    // Check all options in session to see if any match this condition
    for (const optId of sessionRequirementOptions.value) {
      const normalizedOptId = normalizeOptionId(optId);

      // Match the option's owning question by exact normalized ID only. A substring
      // check on optId here leaks across siblings whose IDs share a prefix (e.g. field
      // "22741.1" wrongly matches "22741.10==f"), causing condition_groups for unrelated
      // branches to fire. See LAM-3868.
      const optionPart = optId.split("==")[0];
      const optionWithoutSuffix = optionPart.replace(/_text_input$/, "");
      const normalizedQuestionId = normalizeQuestionId(optionWithoutSuffix);
      const optionBelongsToQuestion = normalizedQuestionId === field;

      if (operator === "eq") {
        // Check if option value matches AND belongs to the correct question
        if (normalizedOptId === value && optionBelongsToQuestion) {
          return true;
        }
      } else if (operator === "ne" && value === "") {
        // Check for text input (option ends with _text_input) AND belongs to the correct question
        if (optId.includes("_text_input") && optionBelongsToQuestion) {
          return true;
        }
      } else if (operator === "ne" && value !== "") {
        // Check if option value does NOT match AND belongs to the correct question
        if (normalizedOptId !== value && optionBelongsToQuestion) {
          return true;
        }
      }
    }
    return false;
  }
}

// Helper: Recursively evaluate a condition group (can have nested condition_groups)
function evaluateConditionGroup(group) {
  const matchType = group.match || "all"; // "any" = OR, "all" = AND

  // Check direct conditions
  const conditionResults = [];
  for (const condition of group.conditions || []) {
    conditionResults.push(isConditionSatisfied(condition));
  }

  // Check nested condition_groups (handle both snake_case and camelCase)
  const nestedGroups = group.condition_groups || group.conditionGroups;
  if (nestedGroups && nestedGroups.length > 0) {
    for (const nestedGroup of nestedGroups) {
      conditionResults.push(evaluateConditionGroup(nestedGroup));
    }
  }

  // Evaluate group based on match type
  if (matchType === "any") {
    // At least one condition must be true (OR)
    return conditionResults.some((result) => result);
  } else {
    // All conditions must be true (AND)
    return conditionResults.every((result) => result);
  }
}

// Helper: Check if condition_groups are satisfied based on answers in session
// Note: Don't rely on existence of answer - if answers are available, all questions have answers
// Instead, check if the answer VALUES match the conditions
function areConditionGroupsSatisfied(portalQuestion) {
  // If no condition_groups, question is always visible
  // Handle both snake_case (condition_groups) and camelCase (conditionGroups)
  const conditionGroups = portalQuestion.condition_groups || portalQuestion.conditionGroups;
  if (!conditionGroups || conditionGroups.length === 0) {
    return true;
  }

  // Check each condition group (groups are ANDed together)
  for (const group of conditionGroups) {
    if (!evaluateConditionGroup(group)) {
      return false; // This group failed
    }
  }

  // All condition groups satisfied
  return true;
}
</script>

<style lang="scss" scoped>
@import "../styles/pages/_patient-information-form.scss";

.policy-flagged-question {
  background-color: rgba(244, 67, 54, 0.04);
  border-left: 3px solid #f44336;
  border-radius: 4px;
  padding-left: 12px !important;
  margin-left: -12px;
}

.policy-warning-inline {
  display: flex;
  align-items: center;
  margin-top: 4px;
}

.policy-warning-text {
  color: #d32f2f;
  font-size: 0.875rem;
  font-weight: 500;
}
</style>
